import { useState, useEffect } from 'react';
import { X, Loader2, ChevronDown, ChevronUp, Download, RefreshCw } from 'lucide-react';
import { votesApi } from '../../api/votes';
import { songsApi } from '../../api/songs';
import { subsetsApi } from '../../api/subsets';
import type { VoteStatistics, SongSummary, SubsetWithSongs } from '../../types';

interface VoteStatsModalProps {
  subsetId: number;
  onClose: () => void;
}

type Tab = 'tally' | 'individual' | 'non-voters' | 'special-categories';

export function VoteStatsModal({ subsetId, onClose }: VoteStatsModalProps) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('tally');
  const [stats, setStats] = useState<VoteStatistics & {
    notVoted: Array<{ id: number; username: string }>;
    specialCategoryTallies: { [categoryIndex: number]: Array<{ songId: number; voteCount: number }> };
  } | null>(null);
  const [subset, setSubset] = useState<SubsetWithSongs | null>(null);
  const [allSongs, setAllSongs] = useState<Map<number, SongSummary>>(new Map());
  const [expandedVotes, setExpandedVotes] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [subsetId]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, songs, subsetData] = await Promise.all([
        votesApi.getAdminStats(subsetId),
        songsApi.getAllSongs(),
        subsetsApi.getSubsetById(subsetId),
      ]);
      setStats(statsData);
      setSubset(subsetData);
      const songsMap = new Map(songs.map(s => [s.id, s]));
      setAllSongs(songsMap);
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to load statistics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const [statsData, subsetData] = await Promise.all([
        votesApi.getAdminStats(subsetId),
        subsetsApi.getSubsetById(subsetId),
      ]);
      setStats(statsData);
      setSubset(subsetData);
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to refresh statistics';
      setError(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async () => {
    if (!stats) return;

    try {
      setExporting(true);
      setExportError(null);
      await votesApi.exportStats(subsetId, stats.subsetName);
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Export failed';
      setExportError(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const toggleVoteExpand = (userId: number) => {
    const newExpanded = new Set(expandedVotes);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedVotes(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes('story')) return '🎭';
    if (name.includes('heart')) return '💝';
    if (name.includes('fun')) return '🎉';
    if (name.includes('energy')) return '⚡';
    if (name.includes('cover')) return '🎨';
    return '⭐';
  };

  const getEnabledCategories = () => {
    return subset?.specialCategories.filter(cat => cat.enabled) || [];
  };

  if (loading) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:max-h-[90vh] bg-monster-card border border-monster-border rounded-lg z-50 flex items-center justify-center">
          <Loader2 className="animate-spin" size={48} />
        </div>
      </>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:max-h-[90vh] bg-monster-card border border-monster-border rounded-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-monster-border">
          <div>
            <h2 className="text-xl font-bold">Vote Statistics</h2>
            <p className="text-sm text-gray-400 mt-1">
              {stats.subsetName} - {stats.votes.length} / {stats.totalVoters} users voted ({Math.round((stats.votes.length / stats.totalVoters) * 100)}% participation)
            </p>
          </div>

          {/* Button group */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="p-2 hover:bg-monster-dark disabled:opacity-50 rounded-lg transition-colors"
              aria-label="Refresh statistics"
              title="Refresh statistics"
            >
              <RefreshCw className={refreshing ? 'animate-spin' : ''} size={20} />
            </button>

            <button
              onClick={handleExport}
              disabled={exporting || loading}
              className="px-4 py-2 bg-monster-blue hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center gap-2"
              aria-label="Export to Excel"
            >
              {exporting ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Export Excel
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
              aria-label="Close"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-600/20 border border-red-600/50 rounded text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Export Error Display */}
        {exportError && (
          <div className="mx-4 mt-2 p-3 bg-red-600/20 border border-red-600/50 rounded text-sm text-red-400">
            Export failed: {exportError}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 px-4 pt-4 border-b border-monster-border">
          <button
            onClick={() => setActiveTab('tally')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'tally'
                ? 'border-monster-blue text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Tally ({stats.tally.length} songs)
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'individual'
                ? 'border-monster-blue text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Individual Votes ({stats.votes.length})
          </button>
          <button
            onClick={() => setActiveTab('non-voters')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'non-voters'
                ? 'border-monster-blue text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Non-Voters ({stats.notVoted.length})
          </button>
          {getEnabledCategories().length > 0 && (
            <button
              onClick={() => setActiveTab('special-categories')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'special-categories'
                  ? 'border-monster-blue text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              Special Categories
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'tally' && (
            <div className="space-y-2">
              {stats.tally.length === 0 ? (
                <div className="text-center text-gray-400 py-8">No votes yet</div>
              ) : (
                <table className="w-full">
                  <thead className="border-b border-monster-border">
                    <tr className="text-left text-sm text-gray-400">
                      <th className="pb-2">Rank</th>
                      <th className="pb-2">Song</th>
                      <th className="pb-2">Artist</th>
                      <th className="pb-2 text-right">Points</th>
                      <th className="pb-2 text-right">Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.tally.map((item, index) => (
                      <tr key={item.songId} className="border-b border-monster-border/50">
                        <td className="py-3">
                          <div className="w-8 h-8 rounded-full bg-monster-blue/20 flex items-center justify-center font-bold text-sm">
                            #{index + 1}
                          </div>
                        </td>
                        <td className="py-3 font-medium">{item.title}</td>
                        <td className="py-3 text-gray-400">{item.artist}</td>
                        <td className="py-3 text-right">
                          <span className="px-2 py-1 bg-monster-blue/20 text-monster-blue rounded-full text-sm font-medium">
                            {item.totalPoints}
                          </span>
                        </td>
                        <td className="py-3 text-right text-gray-400">{item.voteCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'individual' && (
            <div className="space-y-2">
              {stats.votes.length === 0 ? (
                <div className="text-center text-gray-400 py-8">No votes yet</div>
              ) : (
                stats.votes.map((vote) => {
                  const isExpanded = expandedVotes.has(vote.userId);
                  return (
                    <div key={vote.userId} className="border border-monster-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleVoteExpand(vote.userId)}
                        className="w-full flex items-center justify-between p-3 hover:bg-monster-dark transition-colors"
                      >
                        <div>
                          <div className="font-medium">{vote.username}</div>
                          <div className="text-xs text-gray-400">{formatDate(vote.submittedAt)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">{vote.rankedSongIds.length} songs</span>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-monster-border p-3 bg-monster-dark/50">
                          <ol className="space-y-2">
                            {vote.rankedSongIds.map((songId, index) => {
                              const song = allSongs.get(songId);
                              return (
                                <li key={songId} className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-monster-blue flex items-center justify-center font-bold text-sm flex-shrink-0">
                                    #{index + 1}
                                  </div>
                                  {song && (
                                    <>
                                      <img
                                        src={song.imagePath}
                                        alt={song.title}
                                        className="w-10 h-10 rounded object-cover"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate text-sm">{song.title}</div>
                                        <div className="text-xs text-gray-400 truncate">{song.artist}</div>
                                      </div>
                                    </>
                                  )}
                                  {!song && (
                                    <div className="text-sm text-gray-400">Song ID: {songId} (not found)</div>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'non-voters' && (
            <div className="space-y-2">
              {stats.notVoted.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  All users have voted!
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {stats.notVoted.map((user) => (
                    <div
                      key={user.id}
                      className="p-3 bg-monster-dark border border-monster-border rounded"
                    >
                      {user.username}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'special-categories' && (
            <div className="space-y-6">
              {getEnabledCategories().length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No special categories enabled
                </div>
              ) : (
                subset!.specialCategories.map((category, categoryIndex) => {
                  // Skip disabled categories
                  if (!category.enabled) return null;

                  const tallies = stats.specialCategoryTallies[categoryIndex] || [];

                  return (
                    <div key={categoryIndex} className="border border-monster-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-2xl">{getCategoryIcon(category.name)}</span>
                        <h3 className="text-lg font-semibold">{category.name}</h3>
                      </div>

                      {tallies.length === 0 ? (
                        <div className="text-center text-gray-400 py-4">No votes yet</div>
                      ) : (
                        <table className="w-full">
                          <thead className="border-b border-monster-border">
                            <tr className="text-left text-sm text-gray-400">
                              <th className="pb-2">Rank</th>
                              <th className="pb-2">Song</th>
                              <th className="pb-2">Artist</th>
                              <th className="pb-2 text-right">Votes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tallies.map((item, tallyIndex) => {
                              const song = allSongs.get(item.songId);
                              if (!song) return null;

                              return (
                                <tr key={item.songId} className="border-b border-monster-border/50">
                                  <td className="py-3">
                                    <div className="w-8 h-8 rounded-full bg-monster-blue/20 flex items-center justify-center font-bold text-sm">
                                      #{tallyIndex + 1}
                                    </div>
                                  </td>
                                  <td className="py-3 font-medium">{song.title}</td>
                                  <td className="py-3 text-gray-400">{song.artist}</td>
                                  <td className="py-3 text-right">
                                    <span className="px-2 py-1 bg-monster-crimson/20 text-monster-crimson rounded-full text-sm font-medium">
                                      {item.voteCount}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
